import type {
  ModuleClientContribution,
  ModuleManifest,
  ModuleMetadata,
  ModuleServerContribution,
  ModuleServerManifest,
} from "./types";

const ADMIN_NOTIFICATIONS_FEATURE = "admin-notifications";

function declaresAdminNotifications(metadata: ModuleMetadata): boolean {
  return metadata.cmsApiFeatures?.includes(ADMIN_NOTIFICATIONS_FEATURE) === true;
}

function contributionError(metadata: ModuleMetadata, side: "client" | "server"): Error {
  return new Error(
    `[module:${metadata.id}] cmsApiFeatures must declare ${ADMIN_NOTIFICATIONS_FEATURE} `
    + `exactly when the ${side} admin notification contribution is present`,
  );
}

/** Validate imported values instead of guessing from TypeScript source text. */
export function moduleClientManifest(
  metadata: ModuleMetadata,
  contribution: ModuleClientContribution,
  locales: Record<string, Record<string, string>> = {},
): ModuleManifest {
  const hasNotificationNav = contribution.adminNav?.some((item) => Boolean(item.notificationKey)) === true;
  if (declaresAdminNotifications(metadata) !== hasNotificationNav) {
    throw contributionError(metadata, "client");
  }
  return { ...metadata, ...contribution, locales };
}

export function moduleServerManifest(
  metadata: ModuleMetadata,
  contribution: ModuleServerContribution,
  runtime: Pick<ModuleServerManifest, "apiRoutes" | "migrations" | "assetFiles">,
): ModuleServerManifest {
  const hasAdminNotifications = typeof contribution.adminNotifications === "function";
  if (declaresAdminNotifications(metadata) !== hasAdminNotifications) {
    throw contributionError(metadata, "server");
  }
  if ((metadata.cmsApiFeatures?.includes("pages") === true) !== (typeof contribution.pageRoles === "function")) {
    throw new Error(`[module:${metadata.id}] pageRoles requires the pages API feature`);
  }
  return { ...metadata, ...contribution, ...runtime };
}
